import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { useMaskedInput } from '@/shared/lib/useMaskedInput';
import { Calendar } from '@/shared/ui/Calendar/Calendar';
import {
  clampIso,
  completeDateTime,
  formatRuDateTime,
  isLocalTime,
  isoToday,
  joinDateTime,
  maskDateTimeEdit,
  parseRuDateTime,
  splitDateTime,
} from '@/shared/ui/Calendar/calendar-date';
import { Input, type InputProps } from '@/shared/ui/Input/Input';
import { TimeInput } from '@/shared/ui/TimeInput/TimeInput';
import { PickerShell } from './PickerShell';
import styles from './picker.module.css';

/**
 * Поле «дата и время»: `ДД.ММ.ГГГГ ЧЧ:ММ` одной строкой, календарь и часы во
 * всплывашке.
 *
 * Заменяет нативный `datetime-local` — у него те же беды, что у `type="date"`,
 * плюс своя: в части браузеров он вообще не даёт ввести время с клавиатуры без
 * мыши.
 *
 * Значение — `YYYY-MM-DDTHH:mm` в МЕСТНОМ времени, ровно то, что уходило в
 * запросы от `datetime-local`. Ни `Z`, ни секунд: их отсутствие и означает
 * «местное», и менять формат значило бы трогать контракт с бэком.
 *
 * Время внутри всплывашки — обычный `TimeInput`: своя вторая механика ввода
 * часов означала бы два разных способа набрать `14:30` в одном приложении.
 *
 * @use ввод даты со временем одним полем. Вместо `<input type="datetime-local">`.
 */
export interface DateTimePickerProps
  extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'onBlur'> {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  clearable?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

export const DateTimePicker = React.forwardRef<HTMLInputElement, DateTimePickerProps>(
  function DateTimePicker(
    {
      value,
      onChange,
      min,
      max,
      clearable = true,
      className,
      disabled,
      onKeyDown,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState(() => formatRuDateTime(value));
    const parts = splitDateTime(value);

    const [timeText, setTimeText] = React.useState(parts.time);
    React.useEffect(() => {
      setTimeText((current) => (current === parts.time ? current : parts.time));
    }, [parts.time]);

    React.useEffect(() => {
      setText((current) => (parseRuDateTime(current) === value ? current : formatRuDateTime(value)));
    }, [value]);

    const emit = (next: string) => {
      if (next !== value) onChange(next);
    };

    const masked = useMaskedInput({
      value: text,
      mask: maskDateTimeEdit,
      onChange: (next) => {
        setText(next);
        emit(parseRuDateTime(next));
      },
    });
    React.useImperativeHandle(ref, () => masked.ref.current as HTMLInputElement, [masked.ref]);

    const pickDay = (iso: string) => {
      const next = iso === '' ? '' : joinDateTime({ date: iso, time: parts.time });
      setText(formatRuDateTime(next));
      emit(next);
      setOpen(false);
    };

    const pickTime = (time: string) => {
      setTimeText(time);
      if (!isLocalTime(time)) return;
      const date = parts.date || clampIso(isoToday(), min, max);
      const next = joinDateTime({ date, time });
      setText(formatRuDateTime(next));
      emit(next);
    };

    return (
      <PickerShell
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
        filled={text !== ''}
        finalFocus={masked.ref}
        className={className}
        triggerLabel="Открыть календарь"
        onClear={clearable && value !== '' ? () => pickDay('') : undefined}
        clearLabel="Очистить дату и время"
        popup={
          <Calendar
            value={parts.date}
            onSelect={pickDay}
            min={min}
            max={max}
            autoFocusDay
            aria-label="Выбор даты и времени"
            footer={
              <>
                <label className={styles.timeLabel}>
                  <span className={styles.timeCaption}>Время</span>
                  <TimeInput
                    className={styles.time}
                    value={timeText}
                    onChange={pickTime}
                    aria-label="Время"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </label>
              </>
            }
          />
        }
      >
        <Input
          ref={masked.ref}
          className={cn(styles.input)}
          value={text}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          placeholder="дд.мм.гггг чч:мм"
          maxLength={16}
          onChange={masked.onChange}
          onKeyDown={(event) => {
            if (event.altKey && event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              setOpen(false);
            }
            onKeyDown?.(event);
          }}
          onBlur={(event) => {
            const done = completeDateTime(text);
            setText(done);
            emit(parseRuDateTime(done));
            onBlur?.(event);
          }}
          {...rest}
        />
      </PickerShell>
    );
  },
);
